import { groupByPerson } from '@lume/shared';
import { Coffee, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { ConversationCard2B } from './ConversationCard2B';
import { InboxPersonGroup } from './InboxPersonGroup';
import { InboxSectionDivider } from './InboxSectionDivider';

import type { ConversationListItem } from '../../lib/types';

export type InboxEmptyState = 'loading' | 'empty-no-conv' | 'empty-filter' | null;

export interface InboxConvListProps {
  atendendo: ConversationListItem[];
  espera: ConversationListItem[];
  esperaCollapsed: boolean;
  onToggleEspera: () => void;
  emptyState: InboxEmptyState;
  /** Id da conversa selecionada (destaca o card). */
  selectedId?: string | null;
  /** Selecionar uma conversa (card chama com o id). */
  onSelect?: (id: string) => void;
  /** Pegar (claim) uma conversa órfã — card chama quando assignedToUserId == null. */
  onClaim?: (id: string) => void;
  /** Limpa os filtros — usado pelo botão do empty-state "empty-filter". */
  onClearFilters?: () => void;
  /**
   * B1 (spec 2026-08-12 §3.3) — agrupar por pessoa. Gated pela capability
   * `inbox-person-grouping` (o caller decide). false/ausente = render idêntico
   * ao histórico (1 card por conversa).
   */
  groupByPersonEnabled?: boolean;
  /**
   * Alvo B round2 — direção no vazio: quando a aba do operador está vazia mas
   * há fila em «Aguardando atendente», o empty-state aponta para lá com a
   * contagem REAL (`count` vem do agregado do servidor; `onGo` troca a aba).
   * Ausente = copy histórica.
   */
  awaitingHint?: { count: number; onGo: () => void };
}

/**
 * Lista de conversas da Inbox com seções "Em atendimento" / "Em espera".
 * (Brief 2B T1 §6.4)
 *
 * Usa o ConversationCard2B rico (4 linhas). A lista é embrulhada em
 * `.inbox-list`; a seção "Em espera" (pool) em `.inbox-pool` (o CSS já
 * atenua os cards).
 */
export function InboxConvList({
  atendendo,
  espera,
  esperaCollapsed,
  onToggleEspera,
  emptyState,
  selectedId,
  onSelect,
  onClaim,
  onClearFilters,
  groupByPersonEnabled = false,
  awaitingHint,
}: InboxConvListProps): JSX.Element {
  // B1 — renderiza uma seção: agrupada por pessoa (cap ON) ou plana (idêntica
  // ao histórico). Grupo de 1 usa o card puro — o caso comum não muda.
  const renderSection = (list: ConversationListItem[], claimable: boolean): JSX.Element[] => {
    if (!groupByPersonEnabled) {
      return list.map((c) => (
        <ConversationCard2B
          key={c.id}
          conversation={c}
          selected={c.id === selectedId}
          {...(onSelect !== undefined && { onSelect })}
          {...(claimable && onClaim !== undefined && { onClaim })}
        />
      ));
    }
    return groupByPerson(list).map((group) =>
      group.items.length === 1 && group.items[0] !== undefined ? (
        <ConversationCard2B
          key={group.key}
          conversation={group.items[0]}
          selected={group.items[0].id === selectedId}
          {...(onSelect !== undefined && { onSelect })}
          {...(claimable && onClaim !== undefined && { onClaim })}
        />
      ) : (
        <InboxPersonGroup
          key={group.key}
          group={group}
          selectedId={selectedId ?? null}
          {...(onSelect !== undefined && { onSelect })}
          {...(claimable && onClaim !== undefined && { onClaim })}
        />
      ),
    );
  };

  if (emptyState === 'loading') {
    return <LoadingSkeleton />;
  }

  if (emptyState === 'empty-no-conv') {
    return (
      <EmptyState kind="no-conversations" {...(awaitingHint !== undefined && { awaitingHint })} />
    );
  }

  if (emptyState === 'empty-filter') {
    return <EmptyState kind="filtered" {...(onClearFilters !== undefined && { onClearFilters })} />;
  }

  if (atendendo.length === 0 && espera.length === 0) {
    return <EmptyState kind="no-conversations" />;
  }

  return (
    <div>
      {/* Em atendimento */}
      <InboxSectionDivider
        label="Em atendimento"
        count={atendendo.length}
        collapsed={false}
        onToggle={() => {
          /* seção "Em atendimento" sempre visível */
        }}
      />
      <div className="inbox-list">
        {renderSection(atendendo, true)}
        {atendendo.length === 0 && (
          <p className="px-4 py-3 text-sm text-text-secondary italic">
            Nenhuma conversa em atendimento ativo.
          </p>
        )}
      </div>

      {/* Em espera (pool — cards atenuados via .inbox-pool) */}
      <InboxSectionDivider
        label="Em espera"
        count={espera.length}
        collapsed={esperaCollapsed}
        onToggle={onToggleEspera}
      />
      {!esperaCollapsed && (
        <div className="inbox-list inbox-pool">
          {renderSection(espera, false)}
          {espera.length === 0 && (
            <p className="px-4 py-3 text-sm text-text-secondary italic">Fila de espera vazia.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function LoadingSkeleton(): JSX.Element {
  return (
    <ul
      className="flex flex-col divide-y divide-border-subtle"
      aria-busy="true"
      aria-label="Carregando conversas"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="p-3 flex gap-3 items-start">
          {/* Caso A: .skeleton owns bg + pulse animation; width/height/rounded = Caso C layout */}
          <div className="skeleton w-9 h-9 rounded-full shrink-0" />
          <div className="flex-1 space-y-2 pt-0.5">
            <div className="skeleton h-3 w-40 rounded" />
            <div className="skeleton h-3 w-3/4 rounded opacity-60" />
            <div className="skeleton h-3 w-24 rounded opacity-40" />
          </div>
        </li>
      ))}
    </ul>
  );
}

// ─── Empty states ─────────────────────────────────────────────────────────────

function EmptyState({
  kind,
  onClearFilters,
  awaitingHint,
}: {
  kind: 'no-conversations' | 'filtered';
  onClearFilters?: () => void;
  awaitingHint?: { count: number; onGo: () => void };
}): JSX.Element {
  const navigate = useNavigate();

  if (kind === 'no-conversations') {
    return (
      // Caso A: .empty-state owns flex-col centering, padding, gap, color
      <div className="empty-state">
        {/* Caso A: .empty-state__glyph owns shape (48px circle, bg) */}
        <span className="empty-state__glyph" aria-hidden>
          <Coffee className="icon" />
        </span>
        <div>
          {/* Caso A: .empty-state__title owns font-size, font-weight, color */}
          <p className="empty-state__title">
            {awaitingHint !== undefined
              ? 'Nenhuma conversa atribuída a você.'
              : 'Nenhuma conversa atribuída ainda.'}
          </p>
          {/* Caso A: .empty-state__desc owns font-size, max-width, line-height */}
          <p className="empty-state__desc">
            {awaitingHint !== undefined
              ? 'A fila do time tem conversas aguardando atendente — você pode assumir uma.'
              : 'Aguarde — novas conversas aparecem em tempo real.'}
          </p>
        </div>
        {/* Caso A: .empty-state__action owns flex container (display:flex, gap, margin-top) */}
        <div className="empty-state__action">
          {awaitingHint !== undefined && (
            <button type="button" className="btn btn--primary btn--sm" onClick={awaitingHint.onGo}>
              Ver Aguardando atendente ({awaitingHint.count})
            </button>
          )}
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => navigate('/iniciar-conversa')}
          >
            + Iniciar conversa proativa
          </button>
        </div>
      </div>
    );
  }

  return (
    // Caso A: .empty-state owns flex-col centering, padding, gap, color
    <div className="empty-state">
      {/* Caso A: .empty-state__glyph owns shape (48px circle, bg) */}
      <span className="empty-state__glyph" aria-hidden>
        <Search className="icon" />
      </span>
      <div>
        {/* Caso A: .empty-state__title owns font-size, font-weight, color */}
        <p className="empty-state__title">Nenhuma conversa atende aos filtros aplicados.</p>
        {/* Caso A: .empty-state__desc owns font-size, max-width, line-height */}
        <p className="empty-state__desc">Tente limpar os filtros ou alterar a aba.</p>
      </div>
      {/* Caso A: .empty-state__action owns flex container (display:flex, gap, margin-top) */}
      <div className="empty-state__action">
        <button type="button" className="btn btn--secondary btn--sm" onClick={onClearFilters}>
          Limpar filtros
        </button>
      </div>
    </div>
  );
}
